import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Button, Form, DatePicker, ButtonToolbar, Message, Loader, Input, Panel, List, Tag, Divider } from 'rsuite'
import SelectAssemblerPicker from '@components/common/commonFormInput/SelectAssemblerPicker'
import type { GQL_ID } from '@@partner:types:graphql'
import {
  getLastShiftStartTime,
  getLastShiftEndTime,
  saveLastShiftStartTime,
  saveLastShiftEndTime,
  combineDateAndTime
} from '@@partner:helpers/shifts'
import FormCheckbox from '@components/common/form/FormCheckbox'
import { useFetchSingleAssembler } from '@@partner:graphql/query/employees/singleAssembler.gql'
import { WorkShift } from '@@partner:types:app'
import { useMutateUpdateWorkshift } from '@@partner:graphql/mutation/workShift/updateWorkshift'
import { WORKSHIFT } from '@hive/status'

export type ShiftFormData = {
  assemblerId?: GQL_ID
  startDate?: Date
  startTime?: string
  endDate?: Date
  endTime?: string
}

export type ShiftModalMode = 'create' | 'edit'

type AssemblingShiftModalProps = {
  mode?: ShiftModalMode
  isOpen: boolean
  isLoading?: boolean
  shift?: WorkShift | null
  initialDate?: Date | null
  onClose: () => void
  onSave?: () => void
  onEdit?: (shift: WorkShift) => void
}

const defaultFormValue: ShiftFormData = {
  assemblerId: undefined,
  startDate: new Date(),
  startTime: getLastShiftStartTime(),
  endDate: new Date(),
  endTime: getLastShiftEndTime()
}

const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

export default function AssemblingShiftModal ({
  mode = 'create',
  isOpen,
  isLoading,
  shift,
  initialDate,
  onClose,
  onSave,
  onEdit
}: AssemblingShiftModalProps) {
  const [ formValue, setFormValue ] = React.useState<ShiftFormData>(defaultFormValue)
  const [ formError, setFormError ] = useState<Error | null>(null)

  const [ updateWorkshift, { loading, error } ] = useMutateUpdateWorkshift()

  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return {
      date: date.toLocaleDateString('ru-RU'),
      time: date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    }
  }

  const { data: assemblerData, loading: loadingAssembler, error: errorAssembler } = useFetchSingleAssembler({
    id: formValue.assemblerId
  })

  useEffect(() => {
    if (assemblerData?.assembler.id) {
      setFormValue(prev => ({
        ...prev
      }))
    }
  }, [ assemblerData ])

  useEffect(() => {
    if (shift) {
      const startDate = new Date(shift.plannedStartAt)
      const endDate = new Date(shift.plannedFinishAt)
      
      setFormValue({
        assemblerId: shift.assembler_id,
        startDate: startDate,
        startTime: startDate.getHours().toString().padStart(2, '0') + ':' + 
                  startDate.getMinutes().toString().padStart(2, '0'),
        endDate: endDate,
        endTime: endDate.getHours().toString().padStart(2, '0') + ':' + 
                endDate.getMinutes().toString().padStart(2, '0')
      })
    }
    else {
      const baseFormValue = { ...defaultFormValue }
      
      if (initialDate) {
        baseFormValue.startDate = initialDate
        baseFormValue.endDate = initialDate
      }
      
      setFormValue(baseFormValue)
    }
  }, [ shift, initialDate ])
  
  const validateTime = (time?: string): boolean => {
    if (!time) return false
    return timePattern.test(time)
  }
  
  const handleTimeChange = (field: 'startTime' | 'endTime') => (value: string) => {
    if (!value || timePattern.test(value)) {
      setFormValue(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = useCallback(async () => {
    if (!formValue.assemblerId || !formValue.startDate || !formValue.endDate || 
        !validateTime(formValue.startTime) || !validateTime(formValue.endTime)) {
      setFormError(new Error('Пожалуйста, заполните все обязательные поля'))
      return
    }

    try {
      if (formValue.startTime) saveLastShiftStartTime(formValue.startTime)
      if (formValue.endTime) saveLastShiftEndTime(formValue.endTime)

      const plannedStartAt = combineDateAndTime(formValue.startDate, formValue.startTime!)
      const plannedFinishAt = combineDateAndTime(formValue.endDate, formValue.endTime!)
      
      if (plannedFinishAt <= plannedStartAt) {
        setFormError(new Error('Время окончания должно быть больше времени начала'))
        return
      }
      
      await updateWorkshift({
        variables: {
          createWorkShift: !shift,
          workShiftInfoInput: {
            ...(shift?.id && { id: shift.id }),
            assembler_id: formValue.assemblerId,
            plannedStartAt: plannedStartAt.getTime(),
            plannedFinishAt: plannedFinishAt.getTime(),
            status: WORKSHIFT.INITIAL
          }
        }
      })
      setFormError(null)
      if (onSave) { 
        onSave() 
      }
      onClose()
    } 
    catch (error) {
      setFormError(error as Error)
      console.error('Failed to save shift:', error)
    }
  }, [ formValue, shift, onClose, onSave, updateWorkshift ])

  return (
    <Modal open={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>
          {mode === 'edit' ? 'Редактирование смены' : 'Создание смены'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(formError || error) && (
          <Message type="error" className="u-mb-md">
            {formError ? formError.message : error?.message}
          </Message>
        )}
        
        <Form
          fluid
          formValue={formValue}
          onChange={setFormValue}
          onSubmit={handleSubmit}
        >
          <Form.Group>
            <Form.ControlLabel>Сборщик</Form.ControlLabel>
            <Form.Control
              name="assemblerId"
              accepter={SelectAssemblerPicker}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Дата и время начала</Form.ControlLabel>
            <div className="u-flex">
              <div style={{ width: '60%', marginRight: 8 }}>
                <Form.Control
                  name="startDate"
                  oneTap
                  isoWeek
                  accepter={DatePicker}
                  format="dd.MM.yyyy"
                  block
                />
              </div>
              <div style={{ width: '40%' }}>
                <Input 
                  value={formValue.startTime}
                  onChange={handleTimeChange('startTime')}
                  placeholder="HH:MM"
                />
              </div>
            </div>
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Дата и время окончания</Form.ControlLabel>
            <div className="u-flex">
              <div style={{ width: '60%', marginRight: 8 }}>
                <Form.Control
                  name="endDate"
                  oneTap
                  isoWeek
                  accepter={DatePicker}
                  format="dd.MM.yyyy"
                  block
                />
              </div>
              <div style={{ width: '40%' }}>
                <Input 
                  value={formValue.endTime}
                  onChange={handleTimeChange('endTime')}
                  placeholder="HH:MM"
                />
              </div>
            </div>
          </Form.Group>
        </Form>

        {isLoading && <Loader center content="Сохранение..." />}
      </Modal.Body>

      <Modal.Footer>
        <ButtonToolbar>
          <Button appearance="subtle" onClick={onClose}>
            Отмена
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={isLoading}>
            {mode === 'edit' ? 'Сохранить' : 'Создать'}
          </Button>
        </ButtonToolbar>
      </Modal.Footer>
    </Modal>
  )
}